import { ArrowDownToLine, ArrowUpFromLine, Banknote  } from '@lucide/vue'

import { AssetWizardField, AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'
import { TagRef } from '@/entities/tags/utils/types'
import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import {
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import { OWNERSHIP_STEP, SUMMARY_STEP, TAGS_STEP } from '../utils/steps'
import { WizardDefinition, WizardFieldDef } from '../utils/types'

import { LoanDirection, loanSchema } from './schema'
import { create } from './create'

/**
 * The type every record this wizard creates carries. Asking for it would be
 * asking the user to repeat the wizard they just chose — and the answer is not
 * theirs to vary: a mortgage taken against a purchase belongs to the
 * real-estate-with-financing flow, a line of credit to its own.
 *
 * A literal because the app has no asset-type vocabulary to read from — types
 * come from the repository's transaction settings at runtime. One constant, so
 * wiring the real list later is one edit. Not a field: choosing this wizard
 * has already said the type, and showing it would be asking the user to
 * confirm what they cannot change.
 */
const ASSET_TYPE = 'loan'

/**
 * Applied the moment the wizard opens — including when the assistant lands on
 * the summary. Deliberately generic rather than picking a side ('Borrowings'
 * vs a receivable's own class): tags are seeded before the ownership step even
 * renders, which is before `direction` has an answer, so nothing here can
 * depend on which way the money moved.
 */
const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Credit' },
  { tag_name: 'Investment Strategy', tag_value: 'Loan' },
]

const directionField = (): WizardFieldDef => ({
  name: 'direction',
  label: 'Borrowed or lent?',
  control: 'tiles',
  optionsList: [LoanDirection.BORROWED, LoanDirection.LENT],
  // Money's direction relative to the user — in for borrowed, out for lent —
  // rather than two arbitrary glyphs picked to look different from each other.
  tileIcons: {
    [LoanDirection.BORROWED]: ArrowDownToLine,
    [LoanDirection.LENT]: ArrowUpFromLine,
  },
  tileDescriptions: {
    [LoanDirection.BORROWED]: 'This is a debt you owe.',
    [LoanDirection.LENT]: 'This is money owed to you.',
  },
  required: true,
  aiField: AssetWizardField.DIRECTION,
  // Full width in both grids: two tiles side by side need the whole row,
  // whether that row is the step's own two columns or the summary's three.
  span: 2,
  summarySpan: 1,
})

/**
 * One loan, either direction: money borrowed with no asset bought in the same
 * breath (a mortgage taken to buy a property belongs in the
 * real-estate-with-financing wizard, which links the two records; this one
 * stands alone), or money lent out to another party. `direction` is asked
 * first and decides which transaction the record posts — see `records` below
 * and Loan/create.ts.
 */
export const loanWizard: WizardDefinition = {
  flow: AssetWizardFlow.LOAN,
  label: 'Loan',
  icon: Banknote,
  schema: loanSchema,
  steps: [
    {
      key: 'loan',
      label: 'Loan',
      description:
        'A loan is money that changed hands between two parties and is expected to be repaid — borrowed, it is a debt you owe; lent, it is one owed to you.',
      fields: [
        directionField(),
        // Full width: a loan's name is a phrase — "Bank credit line,
        // Northern Trust" — not a figure to fit beside another.
        assetNameField('Name', 'Ex. Bank credit line'),
        currencyField(),
        dateField('Date'),
        amountField('Amount'),
        { ...descriptionField(), span: 2 },
      ],
    },
    // After the loan step, not before: whose account this hangs off reads
    // better once the user has already said what kind of loan it is —
    // borrowed or lent — even though the ownership step's own account-type
    // choices are not narrowed by the answer (see `records[0].transactionType`
    // below, and Wizard.vue's own `transactionType`, for why).
    OWNERSHIP_STEP,
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'loan',
      label: 'Loan',
      icon: Banknote,
      tone: 'brand',
      /*
       * 'add liability' only once the user has explicitly said this was
       * borrowed; 'add position' otherwise, including before `direction` has
       * an answer at all — see Wizard.vue's own `transactionType`, which
       * resolves this against `definition.constants` (no `direction`) while
       * building the form, and needs the same flow's fallback to be the safe
       * (wider-account-types) one rather than the narrower one.
       */
      transactionType: values => values.direction === LoanDirection.BORROWED
        ? TRANSACTION_TYPE.ADD_LIABILITY
        : TRANSACTION_TYPE.ADD_POSITION,
      heading: 'assetName',
      fields: ['direction', 'amount', 'date', 'currency', 'description'],
    },
  ],
  constants: { assetType: ASSET_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
